import { Timestamp } from "@bufbuild/protobuf";

import {
  AdminQuotaUsageSeed,
  AdminQuotaUsageSeed_WindowStart,
  AdminRestriction,
  AdminRestrictionUsageSeed,
  DeleteOrganizationRestrictionOverrideAsAdminRequest,
  DeleteOrganizationRestrictionOverrideAsAdminResponse,
  GetOrgRestrictionUsageHistoryAsAdminRequest,
  GetOrgRestrictionUsageHistoryAsAdminResponse,
  GetOrganizationRestrictionConfigurationAsAdminRequest,
  GetOrganizationRestrictionConfigurationAsAdminResponse,
  GetOrganizationRestrictionsRequest,
  GetOrganizationRestrictionsResponse,
  GetUserRestrictionUsageHistoryAsAdminRequest,
  GetUserRestrictionUsageHistoryAsAdminResponse,
  GetUserRestrictionsAsAdminRequest,
  GetUserRestrictionsAsAdminResponse,
  ListOrgUserRestrictionsAsAdminRequest,
  ListOrgUserRestrictionsAsAdminResponse,
  SeedOrgRestrictionUsageAsAdminRequest,
  SeedOrgRestrictionUsageAsAdminResponse,
  SeedUserRestrictionUsageAsAdminRequest,
  SeedUserRestrictionUsageAsAdminResponse,
  UpdateOrganizationRestrictionAsAdminRequest,
  UpdateOrganizationRestrictionAsAdminResponse,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  GrantSource,
  RestrictionName,
  SortDirection,
  UsageWindowSize,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import {
  ProvisionAICreditPackRequest,
  ProvisionAICreditPackRequest_CreditPack,
  ProvisionAICreditPackResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type GetUserRestrictionsParams = {
  userId: string;
  restrictionNames: string[];
};

export const getUserRestrictions = async (
  { userId, restrictionNames }: GetUserRestrictionsParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetUserRestrictionsAsAdminResponse> => {
  const req = new GetUserRestrictionsAsAdminRequest({
    userId,
    restrictionNames,
  });

  return supportServiceConnectClient.getUserRestrictions(req, { headers });
};

type GetOrgRestrictionsParams = {
  organizationId: string;
  restrictionNames: string[];
};

export const getOrgRestrictions = async (
  { organizationId, restrictionNames }: GetOrgRestrictionsParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetOrganizationRestrictionsResponse> => {
  const req = new GetOrganizationRestrictionsRequest({
    organizationId,
    restrictionNames,
  });

  return supportServiceConnectClient.getOrgRestrictions(req, { headers });
};

type GetOrgRestrictionConfigurationParams = {
  organizationId: string;
  restrictionName: RestrictionName;
};

export const getOrgRestrictionConfiguration = async (
  { organizationId, restrictionName }: GetOrgRestrictionConfigurationParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetOrganizationRestrictionConfigurationAsAdminResponse> => {
  const req = new GetOrganizationRestrictionConfigurationAsAdminRequest({
    organizationId,
    restrictionName,
  });

  return supportServiceConnectClient.getOrgRestrictionConfiguration(req, {
    headers,
  });
};

type UpdateOrgRestrictionParams = {
  organizationId: string;
  restrictionName: string;
  restriction: AdminRestriction;
};

export const updateOrgRestriction = async (
  { organizationId, restrictionName, restriction }: UpdateOrgRestrictionParams,
  headers: Record<RequestMetadata, string>,
): Promise<UpdateOrganizationRestrictionAsAdminResponse> => {
  const req = new UpdateOrganizationRestrictionAsAdminRequest({
    organizationId,
    restrictionName,
    restriction,
  });

  return supportServiceConnectClient.updateOrgRestriction(req, { headers });
};

type DeleteOrgRestrictionOverrideParams = {
  organizationId: string;
  restrictionName: RestrictionName;
};

// Drops the per-org override so the restriction falls back to the tier
// default. ResetOrgRestriction is not the same thing — it also re-anchors the
// quota window against a tier and subscription start date, which Periscope has
// no business choosing.
export const deleteOrgRestrictionOverride = async (
  { organizationId, restrictionName }: DeleteOrgRestrictionOverrideParams,
  headers: Record<RequestMetadata, string>,
): Promise<DeleteOrganizationRestrictionOverrideAsAdminResponse> => {
  const req = new DeleteOrganizationRestrictionOverrideAsAdminRequest({
    organizationId,
    restrictionName,
  });

  return supportServiceConnectClient.deleteOrgRestrictionOverride(req, {
    headers,
  });
};

type ListOrgUserRestrictionsParams = {
  organizationId: string;
  restrictionName: RestrictionName;
  sortBy?: string;
  sortDirection?: SortDirection;
  pageNumber?: number;
  pageSize?: number;
  fromTime?: Date;
  toTime?: Date;
};

export const listOrgUserRestrictions = async (
  {
    organizationId,
    restrictionName,
    sortBy,
    sortDirection,
    pageNumber,
    pageSize,
    fromTime,
    toTime,
  }: ListOrgUserRestrictionsParams,
  headers: Record<RequestMetadata, string>,
): Promise<ListOrgUserRestrictionsAsAdminResponse> => {
  const req = new ListOrgUserRestrictionsAsAdminRequest({
    organizationId,
    restrictionName,
    sortBy,
    sortDirection,
    pageNumber,
    pageSize,
    fromTime: fromTime ? Timestamp.fromDate(fromTime) : undefined,
    toTime: toTime ? Timestamp.fromDate(toTime) : undefined,
  });

  return supportServiceConnectClient.listOrgUserRestrictions(req, { headers });
};

type GetOrgRestrictionUsageHistoryParams = {
  organizationId: string;
  restrictionName: RestrictionName;
  windowSize: UsageWindowSize;
  fromTime: Date;
  toTime: Date;
};

export const getOrgRestrictionUsageHistory = async (
  {
    organizationId,
    restrictionName,
    windowSize,
    fromTime,
    toTime,
  }: GetOrgRestrictionUsageHistoryParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetOrgRestrictionUsageHistoryAsAdminResponse> => {
  const req = new GetOrgRestrictionUsageHistoryAsAdminRequest({
    organizationId,
    restrictionName,
    windowSize,
    fromTime: Timestamp.fromDate(fromTime),
    toTime: Timestamp.fromDate(toTime),
  });

  return supportServiceConnectClient.getOrgRestrictionUsageHistory(req, {
    headers,
  });
};

type GetUserRestrictionUsageHistoryParams = {
  userId: string;
  restrictionName: RestrictionName;
  windowSize: UsageWindowSize;
  fromTime: Date;
  toTime: Date;
};

export const getUserRestrictionUsageHistory = async (
  {
    userId,
    restrictionName,
    windowSize,
    fromTime,
    toTime,
  }: GetUserRestrictionUsageHistoryParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetUserRestrictionUsageHistoryAsAdminResponse> => {
  const req = new GetUserRestrictionUsageHistoryAsAdminRequest({
    userId,
    restrictionName,
    windowSize,
    fromTime: Timestamp.fromDate(fromTime),
    toTime: Timestamp.fromDate(toTime),
  });

  return supportServiceConnectClient.getUserRestrictionUsageHistory(req, {
    headers,
  });
};

type ProvisionAICreditPackParams = {
  organisationId: string;
  creditPack: {
    amount: number;
    grantSource: GrantSource;
    effectiveTo: Date;
  };
};

export const provisionAICreditPack = async (
  { organisationId, creditPack }: ProvisionAICreditPackParams,
  headers: Record<RequestMetadata, string>,
): Promise<ProvisionAICreditPackResponse> => {
  const req = new ProvisionAICreditPackRequest({
    organisationId,
    creditPack: new ProvisionAICreditPackRequest_CreditPack({
      amount: creditPack.amount,
      grantSource: creditPack.grantSource,
      effectiveTo: Timestamp.fromDate(creditPack.effectiveTo),
    }),
  });
  return supportServiceConnectClient.provisionAICreditPack(req, { headers });
};

type SeedRestrictionUsageParams = {
  restrictionName: RestrictionName;
  totalUsage: number;
  eventCount: number;
  windowStart: AdminQuotaUsageSeed_WindowStart;
  feature: string;
  billable: boolean;
};

// buildUsageSeed wraps the per-restrictor seed spec. The server decides how the
// seeded usage is classified (e.g. free vs overage for AI credits) and resolves
// the concrete window; the caller supplies the totals plus where the window
// starts (a quota/credit-pack concept, so it lives on the quota seed).
const buildUsageSeed = ({
  restrictionName,
  totalUsage,
  eventCount,
  windowStart,
  feature,
  billable,
}: SeedRestrictionUsageParams): AdminRestrictionUsageSeed =>
  new AdminRestrictionUsageSeed({
    name: restrictionName,
    restriction: {
      case: "quota",
      value: new AdminQuotaUsageSeed({
        totalUsage: BigInt(totalUsage),
        eventCount,
        windowStart,
        feature,
        billable,
      }),
    },
  });

export const seedUserRestrictionUsage = async (
  { userId, ...params }: SeedRestrictionUsageParams & { userId: string },
  headers: Record<RequestMetadata, string>,
): Promise<SeedUserRestrictionUsageAsAdminResponse> => {
  const req = new SeedUserRestrictionUsageAsAdminRequest({
    userId,
    seed: buildUsageSeed(params),
  });
  return supportServiceConnectClient.seedUserRestrictionUsage(req, { headers });
};

export const seedOrgRestrictionUsage = async (
  {
    organizationId,
    ...params
  }: SeedRestrictionUsageParams & { organizationId: string },
  headers: Record<RequestMetadata, string>,
): Promise<SeedOrgRestrictionUsageAsAdminResponse> => {
  const req = new SeedOrgRestrictionUsageAsAdminRequest({
    organizationId,
    seed: buildUsageSeed(params),
  });
  return supportServiceConnectClient.seedOrgRestrictionUsage(req, { headers });
};
